import { SpaEntityConfigWizard } from '@common/components/SpaEntity/SpaEntityConfigWizard/SpaEntityConfigWizard';
import { SpaEntityConfigWizardProps } from '@common/components/SpaEntity/SpaEntityConfigWizard/SpaEntityConfigWizard.types';
import { SpaEntityFormSteps } from '@common/components/SpaEntity/SpaEntityFormSteps/SpaEntityFormSteps';
import {
  buildSpaEntityFormSchema,
  SpaEntityFormValues,
} from '@common/components/SpaEntity/SpaEntityForm.schema';
import { Routes } from '@common/constants';
import { EntityTypes } from '@common-types/Entities';
import { useGetSpaSettings } from '@features/spa';
import { useGetSpaFacilityTimeslotDetail, useGetSpaFacilityTimeslots } from '@features/spa/hooks';
import { basketSelectors } from '@store/basket/selectors';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { getDaysOfTheWeek } from '@utils/date/daysOfTheWeek';
import { parseDateToString } from '@utils/helpers';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';
import { FacilityFormStepEnum } from '../Facility.types';

export const FacilityConfigWizard: FC<SpaEntityConfigWizardProps> = ({
  spaEntity,
  property,
  language,
  basketDetails,
  bookingStep,
  existingGuests = [],
  signIn,
  handleBasketUpdate,
}) => {
  const router = useRouter();

  const isSpinnerVisible = useSelector(basketSelectors.getIsBasketDetailsFetching);
  const journeyParams = useSelector(yourJourneySelectors.getParsedJourneyParams) as any;

  const isExtrasAvailable = !!(spaEntity?.extras?.length || spaEntity?.selectionGroups?.length);
  const [loginError, setLoginError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const maxDaysAdvance = spaSettings?.maxDaysAdvanceFacilities || 0;
  const [startDate, setStartDate] = useState(
    parseDateToString(journeyParams?.spaDate || journeyParams?.fromDate)
  );

  const form = useForm<SpaEntityFormValues>({
    resolver: zodResolver(buildSpaEntityFormSchema(spaEntity?.selectionGroups)),
    defaultValues: {
      guests: [],
    },
  });

  const selectedTimeSlot = useWatch({ control: form.control, name: 'arrivalTime' });

  const { data: timeslots, isFetching: isFetchingTimeslots } = useGetSpaFacilityTimeslots(
    property.id,
    Number(spaEntity.id),
    startDate,
    language
  );

  const { data: spaFacilityWithTimeslotDetail } = useGetSpaFacilityTimeslotDetail(
    property.id,
    language,
    spaEntity.id,
    (selectedTimeSlot?.startDateTime ?? '') as string
  );

  const currentSpaEntity = spaFacilityWithTimeslotDetail || spaEntity;

  useEffect(() => {
    if (bookingStep) {
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
      form.reset();
    }
  }, [bookingStep, form]);

  const handleSignIn = async (data) => {
    try {
      await signIn(property.id, data);
      setIsSignInModalOpen(false);
    } catch (e) {
      setLoginError('invalidLogin');
    }
  };

  const handleSignInModalClose = () => {
    setIsSignInModalOpen(false);
  };

  const gotoEntityDetailPage = (): Promise<boolean> =>
    router.push({
      pathname: Routes.FACILITY_DETAIL,
      query: {
        id: spaEntity.id,
      },
    });

  const goToStep = (bookingStep: string): Promise<boolean> =>
    router.push({
      pathname: Routes.FACILITY_BOOKING,
      query: {
        id: spaEntity.id,
        bookingStep,
      },
    });

  const goToNextStep = async () => {
    if (bookingStep === FacilityFormStepEnum.AVAILABILITY && isExtrasAvailable) {
      goToStep(FacilityFormStepEnum.CUSTOMISE);
    }
  };

  const goToPreviousStep = () => {
    router.back();
  };

  const showConfirmation = () => {
    setIsConfirmationVisible(true);
  };

  const hideConfirmation = () => {
    setIsConfirmationVisible(false);
  };

  const finishWizardAction = async (data) => {
    await handleBasketUpdate(data);
    showConfirmation();
    await gotoEntityDetailPage();
  };

  const daysToFetch = getDaysOfTheWeek(
    selectedTimeSlot?.startDateTime || parseDateToString(journeyParams?.spaDate)
  );

  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  return (
    <FormProvider {...form}>
      <SpaEntityConfigWizard
        isConfirmationVisible={isConfirmationVisible}
        basketDetails={basketDetails}
        hideConfirmation={hideConfirmation}
        isSignInModalOpen={isSignInModalOpen}
        handleSignInModalClose={handleSignInModalClose}
        spaEntity={currentSpaEntity}
        handleSignIn={handleSignIn}
        loginError={loginError}
        isModalOpen={isModalOpen}
        isSpinnerVisible={!!(isSpinnerVisible || isFetchingTimeslots)}
        goToPreviousStep={goToPreviousStep}
        gotoEntityDetailPage={gotoEntityDetailPage}
        SpaEntityFormSteps={SpaEntityFormSteps}
        bookingStep={bookingStep}
        language={language}
        existingGuests={existingGuests}
        maxDaysAdvance={maxDaysAdvance}
        isExtrasAvailable={isExtrasAvailable}
        goToNextStep={goToNextStep}
        setIsSignInModalOpen={setIsSignInModalOpen}
        finishWizardAction={finishWizardAction}
        selectedTimeSlot={selectedTimeSlot}
        goToStep={goToStep}
        SpaEntityFormStepEnum={FacilityFormStepEnum}
        fetchingDates={isFetchingTimeslots ? daysToFetch : []}
        isSpaEntityFetching={isFetchingTimeslots}
        availableTimeslots={timeslots}
        setStartDate={setStartDate}
        modulesExcludingDisabled={property.modulesExcludingDisabled || {}}
        hideArrivalTimes={hideArrivalTimes}
        entityBookingRoute={Routes.FACILITY_BOOKING}
        entityType={EntityTypes.FACILITY}
        entityLabel="spa.facility"
      />
    </FormProvider>
  );
};
